<script setup lang="ts">
// components
import MenuItem from './MenuItem.vue';
import MenuItemTitle from './MenuItemTitle.vue';

// utils
import isValidURL from '~/utils/isValidURL';

// types
import { type MenuItemWithColumns, MODIFIERS } from '~/components/Menu/types';

defineProps<{ activeItem: MenuItemWithColumns }>();

const localePathWithSlash = useLocalePathWithSlash();

const isExternal = (value: string) => isValidURL(value);
</script>

<template>
  <nav data-component-name="Menu.DesktopDropdown">
    <ul class="columns">
      <li
        v-for="(column, index) of activeItem.columns"
        :key="index"
        class="column"
      >
        <template v-for="item of column" :key="item.i18nKey">
          <MenuItemTitle v-if="!('path' in item)">
            {{ $t(item.i18nKey) }}
          </MenuItemTitle>

          <MenuItem
            v-else
            :to="localePathWithSlash(item.path)"
            :target="isExternal(item.path) ? '_blank' : '_self'"
            :external="isExternal(item.path)"
          >
            {{ $t(item.i18nKey) }}&nbsp;<sup
              v-if="item.modifier === MODIFIERS.NEW"
              class="new"
            >
              {{ $t('NEW') }}
            </sup>
          </MenuItem>
        </template>
      </li>
    </ul>
  </nav>
</template>

<style scoped lang="scss">
@use '_/mixins/flex';
@use '_/mixins/typo';
@use '_/variables/shadow';
@use '_/fn';

[data-component-name='Menu.DesktopDropdown'] {
  padding: 2rem;
  box-shadow: shadow.$elevation-3;
  background-color: fn.token('surf-cont-primary');

  ul.columns {
    @include flex.stretch-sb;
    gap: 0 2rem;

    li.column {
      @include flex.stretch-start;
      flex-direction: column;
      width: 100%;

      sup.new {
        color: fn.token('text-error');
        transform: translateY(-0.5rem);
        @include typo.subtitle-3;
      }
    }
  }
}
</style>
